/**
 * il Mosaico (flywheel-workspace-governance ADR-045): the portal client for spazi, tessere,
 * TiddlyWiki filters and atoms. Calls go through the session gateway, which names the
 * signed-in person, so riservato and personal spazi work here.
 */
import { KERNEL_API_URL } from "../../config";

export type Visibilita = "privato" | "riservato" | "aziendale" | "pubblico";
export interface SpazioInfo {
  slug: string; titolo: string; descrizione: string; visibilita: Visibilita; personale: boolean;
  home: string | null; mio: boolean; ruolo: "legge" | "scrive" | "cura" | null; write: boolean; cura: boolean; tessere: number;
}
export interface TesseraHit {
  spazio: string; titolo: string; estratto?: string; tags?: string[]; stato?: string; modificata?: string; da?: string; mancante?: boolean;
}
/** `titolo` is the target's current title; `scritto` the one the text still writes, after a rename. */
export interface LinkRef { accessibile: boolean; spazio?: string; titolo?: string; esiste?: boolean; testo?: string | null; id?: string; scritto?: string }
export interface Revisione { rev: number; at: string; autore: string | null; titolo: string; tags: string[]; nota: string | null; caratteri?: number; testo?: string }
export interface Tessera {
  recall_id: string; spazio: string; titolo: string; testo: string; tags: string[]; stato: string; revisione: number;
  source_refs: string[]; modificata: string; da: string; write: boolean; cura: boolean;
  backlinks: { spazio: string; titolo: string }[]; tagged: string[]; links: LinkRef[]; transclusioni: LinkRef[];
  inclusa_in: { spazio: string; titolo: string }[]; stessi_tag: { titolo: string; comuni: number }[]; home: boolean;
  /** Identity (ADR-045 amendment 2026-10-31): the id is stable; old titles stay as aliases. */
  id: string; alias?: string[]; rinominata_da?: string;
  /** For those who write: the published revision and whether the working draft is ahead of it. */
  pubblicata_rev?: number | null; modifiche_non_pubblicate?: boolean;
}
export interface MissingTessera { spazio: string; titolo: string; mancante: true; tagged: string[]; inaccessibile?: boolean }
export type MapNodeTipo = "tessera" | "bozza" | "articolo" | "tag" | "mancante";
export interface MapNode { id: string; spazio: string; titolo: string; tipo: MapNodeTipo; stato?: string }
export interface MapEdge { source: string; target: string; relation: "link" | "transclusione" | "tag" }
export interface AtomNode {
  ref: string; type: string; title: string; body: string; source: string; observed: string; confirmations: number; fitness: string | null;
  /** The tessera shape (ADR-045 amendment 2026-10-31): carried over one to one on promotion. */
  tags?: string[]; campi?: Record<string, string>; source_refs?: string[];
}

async function call<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const res = await fetch(`${KERNEL_API_URL}/mosaico/${path}`, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error((data as { error?: string }).error ?? `HTTP ${res.status}`), { status: res.status, data });
  return data as T;
}
const e = encodeURIComponent;
/**
 * The same-origin gateway decodes route parameters once before it forwards them to Fastify.
 * A literal percent would therefore become a bare `%` in the upstream URL and Fastify rejects
 * the request as FST_ERR_BAD_URL. Protect percent escapes for values used in path segments;
 * query-string values keep normal URL encoding.
 */
const segment = (value: string) => e(value).replace(/%25/gi, "%2525");

export const mosaico = {
  spazi: () => call<{ spazi: SpazioInfo[] }>("spazi").then((r) => r.spazi),
  creaSpazio: (b: { slug: string; titolo: string; visibilita: Visibilita; descrizione?: string }) => call("spazi", "POST", b),
  membro: (slug: string, membro: string, ruolo: string | null) => call(`spazi/${e(slug)}/membri`, "PUT", { membro, ruolo }),
  /** The whole text and campi of every hit (the column board edits what it lists). */
  filtroPieno: (f: string, spazio: string) =>
    call<{ tessere: (TesseraHit & { testo: string; tags: string[]; campi: Record<string, unknown> })[] }>(`filtro?f=${e(f)}&spazio=${e(spazio)}&pieno=1&limit=1000`).then((r) => r.tessere.filter((h) => !h.mancante)),
  filtro: (f: string, spazio?: string) =>
    call<{ totale: number; tessere: TesseraHit[]; mancanti: number; recall_id: string }>(`filtro?f=${e(f)}${spazio ? `&spazio=${e(spazio)}` : ""}`),
  tag: (slug: string) => call<{ tag: { tag: string; n: number; ha_tessera: boolean }[] }>(`spazi/${e(slug)}/tag`).then((r) => r.tag),
  /** A tessera, or a missing one (with what a tag of that name lists). */
  apri: async (spazio: string, titolo: string): Promise<Tessera | MissingTessera> => {
    try {
      return await call<Tessera | MissingTessera>(`spazi/${segment(spazio)}/tessere/${segment(titolo)}`);
    } catch (err) {
      const x = err as { status?: number; data?: { inaccessibile?: boolean } };
      if (x.status === 404) return { spazio, titolo, mancante: true, tagged: [], inaccessibile: !!x.data?.inaccessibile };
      throw err;
    }
  },
  /** `id` saves to that tessera whatever its title; `based_on` turns a stale save into a 409 with the other version. */
  salva: (spazio: string, titolo: string, b: { titolo?: string; testo: string; tags: string[]; campi?: Record<string, unknown>; source_refs?: string[]; nota?: string; id?: string; based_on?: number }) =>
    call<{ id: string; spazio: string; titolo: string; revisione: number }>(`spazi/${segment(spazio)}/tessere/${segment(titolo)}`, "PUT", b),
  revisioni: (spazio: string, titolo: string) =>
    call<{ id: string; revisione: number; revisioni: Revisione[] }>(`spazi/${segment(spazio)}/tessere/${segment(titolo)}/revisioni`),
  revisione: (spazio: string, titolo: string, rev: number) => call<Revisione>(`spazi/${segment(spazio)}/tessere/${segment(titolo)}/revisioni/${rev}`),
  ripristina: (spazio: string, titolo: string, rev: number, based_on: number) =>
    call<{ revisione: number }>(`spazi/${segment(spazio)}/tessere/${segment(titolo)}/revisioni/${rev}/ripristina`, "POST", { based_on }),
  /** Permalink: the tessera behind an id, if the reader may see it. */
  perId: (id: string) => call<{ id: string; spazio: string; titolo: string; stato: string }>(`tessere/by-id/${e(id)}`),
  stato: (spazio: string, titolo: string, stato: string) => call(`spazi/${segment(spazio)}/tessere/${segment(titolo)}/stato`, "POST", { stato }),
  aggiornaSpazio: (slug: string, b: { home?: string | null; titolo?: string; descrizione?: string }) => call(`spazi/${e(slug)}`, "PUT", b),
  titoli: (spazio?: string) =>
    call<{ titoli: { spazio: string; titolo: string }[] }>(`titoli${spazio ? `?spazio=${e(spazio)}` : ""}`).then((r) => r.titoli),
  /** The stitches: the neighbourhood of one tessera (centro), a filter, or a whole spazio. */
  mappa: (q: { spazio?: string; f?: string; centro?: string; depth?: number }) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
    return call<{ nodes: MapNode[]; edges: MapEdge[]; troncata: boolean }>(`mappa?${p}`);
  },
  atomi: (f: string, grafo = false) =>
    call<{ totale: number; atomi: AtomNode[]; edges?: { source: string; target: string }[]; troncato?: boolean }>(`atomi?f=${e(f)}${grafo ? "&grafo=1" : ""}`),
};

export const isMissing = (t: Tessera | MissingTessera): t is MissingTessera => (t as MissingTessera).mancante === true;

/**
 * The Mosaic stores links as wiki references, but that is deliberately not its authoring format.
 * The editor receives ordinary Markdown links and this boundary converts only `tessera:` URLs.
 */
export const mosaicoToMarkdown = (testo: string, spazio: string, slugs: Set<string>) => testo.replace(/\[\[([^\[\]]+?)\]\]/g, (_all, raw: string) => {
  const bar = raw.lastIndexOf("|");
  const label = (bar < 0 ? raw : raw.slice(0, bar)).trim();
  const target = (bar < 0 ? raw : raw.slice(bar + 1)).trim();
  const slash = target.indexOf("/");
  const targetSpazio = slash > 0 && slugs.has(target.slice(0, slash)) ? target.slice(0, slash) : spazio;
  const titolo = targetSpazio === spazio ? target : target.slice(slash + 1);
  return `[${label}](tessera:${encodeURIComponent(targetSpazio)}/${encodeURIComponent(titolo)})`;
});

/** Keep legacy includes and deliberately-entered references intact during the Markdown round-trip. */
const unescapeStorageSyntax = (markdown: string) => markdown
  .replace(/\\\[\\\[(.+?)\\\]\\\]/g, "[[$1]]")
  .replace(/\\\{\\\{(.+?)\\\}\\\}/g, "{{$1}}")
  .replace(/\\\[\[(.+?)\]\\\]/g, "[[$1]]");

export const markdownToMosaico = (markdown: string, spazio: string) => unescapeStorageSyntax(markdown.replace(/\[([^\]]*)\]\(tessera:([^\s)]+)\)/g, (_all, label: string, raw: string) => {
  const slash = raw.indexOf("/");
  if (slash < 1) return _all;
  try {
    const targetSpazio = decodeURIComponent(raw.slice(0, slash));
    const titolo = decodeURIComponent(raw.slice(slash + 1));
    const target = targetSpazio === spazio ? titolo : `${targetSpazio}/${titolo}`;
    return label === titolo ? `[[${target}]]` : `[[${label}|${target}]]`;
  } catch { return _all; }
}));
